import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Small } from "@/components/typography";
import { Textarea } from "@/components/ui/textarea";
import { usePatchPage } from "../hooks/use-review";
import { useAutosave, type SaveStatus } from "../lib/use-autosave";
import { PAGE_FLAGS } from "../lib/flags";
import { FlagPicker } from "./flag-picker";
import type { ReviewPage } from "../lib/review-client";

/** Render with `key={page.id}` so switching the selected thumb resets
 * local state instead of carrying stale flags/notes across pages. */
export function ReviewPageMeta({
  scriptId,
  page,
  onAutosaveChange,
}: {
  scriptId: string;
  page: ReviewPage;
  onAutosaveChange?: (controller: { status: SaveStatus; flush: () => Promise<void> }) => void;
}) {
  const patchPage = usePatchPage(scriptId);
  const [flags, setFlags] = useState(page.flags);
  const [notes, setNotes] = useState(page.notes ?? "");
  const autosave = useAutosave<{ flags?: string[]; notes?: string | null }>(patch =>
    patchPage.mutateAsync({ pageId: page.id, patch }),
  );

  useEffect(() => {
    onAutosaveChange?.({ status: autosave.status, flush: autosave.flush });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosave.status, autosave.flush]);

  function onFlagsChange(next: string[]) {
    setFlags(next);
    autosave.schedule({ flags: next });
  }

  function onNotesChange(next: string) {
    setNotes(next);
    autosave.schedule({ notes: next || null });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Page flags</Label>
        <FlagPicker vocab={PAGE_FLAGS} value={flags} onChange={onFlagsChange} />
      </div>
      <div className="space-y-1.5">
        <Label>Page notes</Label>
        <Textarea value={notes} onChange={e => onNotesChange(e.target.value)} rows={3} />
      </div>
      <SaveStatusLabel status={autosave.status} />
    </div>
  );
}

export function SaveStatusLabel({ status }: { status: "idle" | "saving" | "saved" | "error" }) {
  if (status === "idle") return null;
  return (
    <Small className={status === "error" ? "text-destructive" : "text-muted-foreground"}>
      {status === "saving" ? "Saving…" : status === "saved" ? "Saved" : "Error saving"}
    </Small>
  );
}
